// ---------------------------------------------------------------------
// visitChainWires — pure geometry helper for the appointment board's
// node-editor style wires. Given the plan items, returns the list of
// wires to draw: one per consecutive visit pair of the same multi-visit
// procedure. The board's VisitChainWires overlay measures the real card
// DOM positions and draws a bezier between each pair's endpoints.
//
// Card key convention matches the board render:
//   `${item.id}:${visit.id}`   (single-visit item-only cards never chain)
// ---------------------------------------------------------------------

const NON_CHAINABLE_ITEM_STATUS = new Set(["Cancelled", "Declined"]);

export function chainKeyFor(itemId, visitId) {
  return `${itemId}:${visitId}`;
}

// A visit renders in the "Needs appointment" lane until it has an
// effective appointment phase (its own `phase_number`, or the item's as a
// fallback) — the same rule the board uses to bucket cards into columns.
// Returns the positive phase number, or null when the card is still
// unplaced.
export function visitAppointmentPhase(visit, item) {
  const raw = visit?.phase_number ?? item?.phase_number;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function buildVisitChainWires(items = []) {
  const wires = [];
  for (const item of items || []) {
    if (!item || item.is_amendment) continue;
    if (NON_CHAINABLE_ITEM_STATUS.has(item.status)) continue;

    const visits = (Array.isArray(item.procedure_visits) ? [...item.procedure_visits] : [])
      .filter((v) => v && v.id != null)
      .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0));
    if (visits.length < 2) continue;

    for (let i = 0; i < visits.length - 1; i++) {
      const from = visits[i];
      const to = visits[i + 1];
      // Only connect two visits that are BOTH placed on an appointment.
      // A visit still sitting in "Needs appointment" (no phase) shows no
      // wire until it's moved onto an actual appointment column.
      if (visitAppointmentPhase(from, item) == null) continue;
      if (visitAppointmentPhase(to, item) == null) continue;
      wires.push({
        itemId:  item.id,
        fromKey: chainKeyFor(item.id, from.id),
        toKey:   chainKeyFor(item.id, to.id),
        fromSeq: Number(from.sequence) || i + 1,
        toSeq:   Number(to.sequence) || i + 2,
      });
    }
  }
  return wires;
}
